<template>
    <div>
        <v-card outlined class="pa-4 mb-4">
            <div class="d-flex flex-wrap align-center justify-space-between">
                <div>
                    <div class="secao-titulo">Receitas de {{ mesFormatado }}</div>
                    <div class="caption grey--text text--darken-1">
                        Os atendimentos vêm da agenda. Complete pagamento, desconto e status —
                        taxa, comissão e líquido são calculados.
                    </div>
                </div>

                <div class="d-flex flex-wrap align-center mt-3 mt-md-0">
                    <v-btn
                        color="teal darken-2"
                        dark
                        class="mr-2 mb-2"
                        :loading="importando"
                        @click="dialogImportar = true"
                    >
                        <v-icon left>mdi-calendar-import</v-icon>
                        Importar da agenda
                    </v-btn>

                    <v-btn
                        color="orange darken-3"
                        outlined
                        class="mr-2 mb-2"
                        :loading="recalculando"
                        title="Reconcilia o valor bruto com o preço que está na agenda"
                        @click="recalcular"
                    >
                        <v-icon left>mdi-calculator-variant</v-icon>
                        Recalcular pela agenda
                    </v-btn>

                    <v-btn color="primary" outlined class="mr-2 mb-2" @click="abrirNovo">
                        <v-icon left>mdi-plus</v-icon>
                        Lançamento livre
                    </v-btn>

                    <v-btn
                        color="green darken-2"
                        dark
                        class="mb-2"
                        :disabled="!temAlteracoes"
                        :loading="salvando"
                        @click="salvarAlteracoes"
                    >
                        <v-icon left>mdi-content-save</v-icon>
                        Salvar {{ idsAlterados.length ? `(${idsAlterados.length})` : '' }}
                    </v-btn>
                </div>
            </div>
        </v-card>

        <v-card outlined class="pa-4 mb-4">
            <v-row dense>
                <v-col cols="12" sm="4" md="3">
                    <v-select
                        v-model="filtroStatus"
                        :items="['Todos', ...statusReceita]"
                        label="Status"
                        dense
                        outlined
                        hide-details
                    ></v-select>
                </v-col>
                <v-col cols="12" sm="4" md="3">
                    <v-select
                        v-model="filtroBarbeiro"
                        :items="opcoesBarbeiro"
                        item-text="nome"
                        item-value="id"
                        label="Barbeiro"
                        dense
                        outlined
                        hide-details
                    ></v-select>
                </v-col>
                <v-col cols="12" sm="4" md="3">
                    <v-text-field
                        v-model="busca"
                        label="Buscar cliente ou serviço"
                        prepend-inner-icon="mdi-magnify"
                        dense
                        outlined
                        clearable
                        hide-details
                    ></v-text-field>
                </v-col>
                <v-col cols="12" md="3" class="d-flex align-center">
                    <v-alert
                        v-if="temAlteracoes"
                        dense
                        text
                        type="info"
                        class="mb-0 caption py-1"
                    >
                        {{ idsAlterados.length }} linha(s) alterada(s) ainda não salva(s).
                    </v-alert>
                </v-col>
            </v-row>
        </v-card>

        <v-card outlined>
            <v-data-table
                :headers="headers"
                :items="linhasFiltradas"
                :items-per-page="25"
                :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
                dense
                no-data-text="Nenhuma receita lançada neste mês. Use 'Importar da agenda' para começar."
                class="tabela-receitas"
            >
                <template v-slot:item.data="{ item }">
                    <v-text-field
                        v-model="item.data"
                        type="date"
                        dense
                        hide-details
                        class="campo-tabela campo-data"
                        @change="marcarAlterado(item)"
                    ></v-text-field>
                </template>

                <template v-slot:item.cliente="{ item }">
                    <div class="celula-texto">
                        <div class="font-weight-medium">{{ item.cliente || '—' }}</div>
                        <div class="caption grey--text">{{ item.servico || 'Sem serviço' }}</div>
                    </div>
                </template>

                <template v-slot:item.barbeiroNome="{ item }">
                    <span class="body-2">{{ item.barbeiroNome || '—' }}</span>
                </template>

                <template v-slot:item.categoria="{ item }">
                    <v-select
                        v-model="item.categoria"
                        :items="config.categoriasServico"
                        dense
                        hide-details
                        class="campo-tabela campo-select"
                        @change="marcarAlterado(item)"
                    ></v-select>
                </template>

                <template v-slot:item.pagamento="{ item }">
                    <v-select
                        v-model="item.pagamento"
                        :items="config.formasPagamento"
                        dense
                        hide-details
                        clearable
                        class="campo-tabela campo-select"
                        @change="marcarAlterado(item)"
                    ></v-select>
                </template>

                <template v-slot:item.valorBruto="{ item }">
                    <campo-valor
                        v-model="item.valorBruto"
                        prefix="R$"
                        dense
                        hide-details
                        class="campo-tabela campo-valor"
                        @change="marcarAlterado(item)"
                    ></campo-valor>
                </template>

                <template v-slot:item.desconto="{ item }">
                    <campo-valor
                        v-model="item.desconto"
                        prefix="R$"
                        dense
                        hide-details
                        class="campo-tabela campo-valor"
                        @change="marcarAlterado(item)"
                    ></campo-valor>
                </template>

                <template v-slot:item.status="{ item }">
                    <v-select
                        v-model="item.status"
                        :items="statusReceita"
                        dense
                        hide-details
                        class="campo-tabela campo-select"
                        @change="marcarAlterado(item)"
                    ></v-select>
                </template>

                <template v-slot:item.taxaValor="{ item }">
                    <span class="caption">{{ formatarMoeda(calcular(item).taxaValor) }}</span>
                </template>

                <template v-slot:item.comissao="{ item }">
                    <span class="caption">
                        {{ formatarMoeda(calcular(item).comissao) }}
                        <span class="grey--text">({{ formatarPercentual(calcular(item).percentualComissao) }})</span>
                    </span>
                </template>

                <template v-slot:item.liquido="{ item }">
                    <span class="font-weight-bold teal--text text--darken-3">
                        {{ formatarMoeda(calcular(item).liquido) }}
                    </span>
                </template>

                <template v-slot:item.acoes="{ item }">
                    <v-btn icon small color="red" title="Excluir lançamento" @click="remover(item)">
                        <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                </template>
            </v-data-table>

            <v-divider></v-divider>

            <div class="pa-4 totais">
                <div>
                    <div class="caption grey--text text--darken-1">Bruto</div>
                    <div class="font-weight-bold">{{ formatarMoeda(totais.valorBruto) }}</div>
                </div>
                <div>
                    <div class="caption grey--text text--darken-1">Descontos</div>
                    <div class="font-weight-bold">{{ formatarMoeda(totais.desconto) }}</div>
                </div>
                <div>
                    <div class="caption grey--text text--darken-1">Taxas</div>
                    <div class="font-weight-bold">{{ formatarMoeda(totais.taxaValor) }}</div>
                </div>
                <div>
                    <div class="caption grey--text text--darken-1">Comissões</div>
                    <div class="font-weight-bold">{{ formatarMoeda(totais.comissao) }}</div>
                </div>
                <div>
                    <div class="caption grey--text text--darken-1">Líquido da barbearia</div>
                    <div class="font-weight-bold teal--text text--darken-3">{{ formatarMoeda(totais.liquido) }}</div>
                </div>
            </div>
        </v-card>

        <!-- Importar da agenda -->
        <v-dialog v-model="dialogImportar" max-width="520">
            <v-card>
                <v-card-title class="headline">Importar atendimentos da agenda</v-card-title>
                <v-card-text>
                    <p class="body-2">
                        Traz para {{ mesFormatado }} os atendimentos da agenda que ainda não têm
                        lançamento financeiro. Atendimentos já importados são ignorados, então
                        pode rodar quantas vezes quiser.
                    </p>

                    <v-select
                        v-model="statusImportacao"
                        :items="statusReceita"
                        label="Status inicial dos lançamentos"
                        outlined
                        dense
                    ></v-select>

                    <v-alert dense text type="info" class="caption mb-0">
                        A categoria é sugerida pelo nome do serviço. Ao salvar a tabela, a categoria
                        escolhida fica memorizada para as próximas importações daquele serviço.
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialogImportar = false">Cancelar</v-btn>
                    <v-btn color="teal darken-2" dark :loading="importando" @click="importar">
                        Importar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Lançamento livre -->
        <v-dialog v-model="dialogNovo" max-width="620">
            <v-card>
                <v-card-title class="headline">Lançamento livre</v-card-title>
                <v-card-text>
                    <p class="body-2 grey--text text--darken-1">
                        Só o valor e a data são obrigatórios. Use para entradas que não passaram
                        pela agenda — venda de produto, pacote, gorjeta, serviço avulso.
                    </p>

                    <v-form ref="formNovo">
                        <v-row dense>
                            <v-col cols="12" sm="4">
                                <campo-valor
                                    v-model="novo.valorBruto"
                                    prefix="R$"
                                    label="Valor"
                                    outlined
                                    dense
                                    autofocus
                                    :rules="[valorValido]"
                                ></campo-valor>
                            </v-col>
                            <v-col cols="12" sm="8">
                                <v-text-field
                                    v-model="novo.servico"
                                    label="Descrição do serviço"
                                    placeholder="Ex.: pacote fechado, venda de pomada"
                                    outlined
                                    dense
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="novo.data"
                                    type="date"
                                    label="Data"
                                    outlined
                                    dense
                                    :rules="[obrigatorio]"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-select
                                    v-model="novo.pagamento"
                                    :items="config.formasPagamento"
                                    label="Forma de pagamento"
                                    outlined
                                    dense
                                    clearable
                                ></v-select>
                            </v-col>

                            <v-col cols="12">
                                <v-divider class="mb-3"></v-divider>
                                <div class="caption grey--text text--darken-1 mb-2">
                                    Opcionais — preencha o barbeiro apenas se este valor deve gerar comissão.
                                </div>
                            </v-col>

                            <v-col cols="12" sm="6">
                                <v-select
                                    v-model="novo.barbeiroId"
                                    :items="barbeiros"
                                    item-text="nome"
                                    item-value="id"
                                    label="Barbeiro (opcional)"
                                    outlined
                                    dense
                                    clearable
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="novo.cliente"
                                    label="Cliente / referência (opcional)"
                                    outlined
                                    dense
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-select
                                    v-model="novo.categoria"
                                    :items="config.categoriasServico"
                                    label="Categoria"
                                    outlined
                                    dense
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <campo-valor
                                    v-model="novo.desconto"
                                    prefix="R$"
                                    label="Desconto"
                                    outlined
                                    dense
                                ></campo-valor>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-select
                                    v-model="novo.status"
                                    :items="statusReceita"
                                    label="Status"
                                    outlined
                                    dense
                                ></v-select>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="novo.observacao"
                                    label="Observação"
                                    outlined
                                    dense
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-form>

                    <v-alert dense text type="info" class="caption mb-0">
                        Sem barbeiro não há comissão, e o valor aparece como
                        "Sem barbeiro (lançamentos livres)" no resultado do mês.
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="dialogNovo = false">Cancelar</v-btn>
                    <v-btn color="primary" :loading="salvando" @click="criar">Salvar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import {
    salvarReceita,
    salvarReceitasEmLote,
    removerReceita,
    importarDaAgenda,
    recalcularValoresDaAgenda,
    salvarConfig
} from '@/services/financeiro'
import {
    STATUS_RECEITA,
    calcularReceita,
    formatarMoeda,
    formatarPercentual,
    numero
} from '@/services/financeiroCalculos'
import CampoValor from './CampoValor'

export default {
    components: { CampoValor },

    props: ['mes', 'receitas', 'config', 'comissoes', 'barbeiros', 'usuario'],

    data(){
        return {
            linhas: [],
            idsAlterados: [],
            statusReceita: STATUS_RECEITA,
            filtroStatus: 'Todos',
            filtroBarbeiro: 'todos',
            busca: '',
            salvando: false,
            importando: false,
            recalculando: false,
            dialogImportar: false,
            dialogNovo: false,
            statusImportacao: 'Recebido',
            novo: this.lancamentoVazio(),
            headers: [
                { text: 'Data', value: 'data', width: 150 },
                { text: 'Cliente / serviço', value: 'cliente' },
                { text: 'Barbeiro', value: 'barbeiroNome' },
                { text: 'Categoria', value: 'categoria', width: 150 },
                { text: 'Pagamento', value: 'pagamento', width: 140 },
                { text: 'Bruto', value: 'valorBruto', width: 120 },
                { text: 'Desconto', value: 'desconto', width: 120 },
                { text: 'Status', value: 'status', width: 140 },
                { text: 'Taxa', value: 'taxaValor', sortable: false },
                { text: 'Comissão', value: 'comissao', sortable: false },
                { text: 'Líquido', value: 'liquido', sortable: false },
                { text: '', value: 'acoes', sortable: false, align: 'end' }
            ]
        }
    },

    computed: {
        mesFormatado(){
            if(!this.mes){
                return ''
            }
            const [ano, mes] = this.mes.split('-')
            const nomes = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro']
            return `${nomes[Number(mes) - 1]}/${ano}`
        },

        temAlteracoes(){
            return this.idsAlterados.length > 0
        },

        opcoesBarbeiro(){
            return [{ id: 'todos', nome: 'Todos os barbeiros' }, ...this.barbeiros]
        },

        linhasFiltradas(){
            const termo = (this.busca || '').toLowerCase()

            return this.linhas.filter((item) => {
                if(this.filtroStatus !== 'Todos' && item.status !== this.filtroStatus){
                    return false
                }
                if(this.filtroBarbeiro !== 'todos' && item.barbeiroId !== this.filtroBarbeiro){
                    return false
                }
                if(termo){
                    const alvo = `${item.cliente || ''} ${item.servico || ''}`.toLowerCase()
                    return alvo.includes(termo)
                }
                return true
            })
        },

        totais(){
            return this.linhasFiltradas
                .map((item) => this.calcular(item))
                .filter((item) => item.status === 'Recebido')
                .reduce((acc, item) => ({
                    valorBruto: acc.valorBruto + numero(item.valorBruto),
                    desconto: acc.desconto + numero(item.desconto),
                    taxaValor: acc.taxaValor + numero(item.taxaValor),
                    comissao: acc.comissao + numero(item.comissao),
                    liquido: acc.liquido + numero(item.liquido)
                }), { valorBruto: 0, desconto: 0, taxaValor: 0, comissao: 0, liquido: 0 })
        }
    },

    watch: {
        receitas: {
            immediate: true,
            handler(){
                this.sincronizarLinhas()
            }
        }
    },

    methods: {
        formatarMoeda,
        formatarPercentual,

        lancamentoVazio(){
            return {
                data: '',
                cliente: '',
                barbeiroId: '',
                barbeiroNome: '',
                servico: '',
                categoria: 'Outro',
                pagamento: '',
                valorBruto: 0,
                desconto: 0,
                status: 'Recebido',
                observacao: '',
                origem: 'manual'
            }
        },

        obrigatorio(valor){
            return !!valor || 'Campo obrigatório'
        },

        valorValido(valor){
            return numero(valor) > 0 || 'Informe um valor maior que zero'
        },

        sincronizarLinhas(){
            this.linhas = (this.receitas || []).map((item) => ({ ...item }))
            this.idsAlterados = []
        },

        calcular(item){
            return calcularReceita(item, this.config, this.comissoes)
        },

        marcarAlterado(item){
            if(!this.idsAlterados.includes(item.id)){
                this.idsAlterados.push(item.id)
            }
        },

        async salvarAlteracoes(){
            if(!this.temAlteracoes){
                return
            }

            this.salvando = true
            try {
                const alteradas = this.linhas.filter((item) => this.idsAlterados.includes(item.id))
                await salvarReceitasEmLote(alteradas, this.usuario)
                await this.memorizarCategorias(alteradas)

                this.$toasted.global.defaultSuccess(`${alteradas.length} lançamento(s) salvo(s)!`)
                this.idsAlterados = []
                this.$emit('recarregar')
            } catch (error) {
                this.$toasted.global.defaultError({ msg: error.message })
            } finally {
                this.salvando = false
            }
        },

        // Guarda a categoria escolhida por servico para que a proxima importacao
        // da agenda ja venha classificada corretamente.
        async memorizarCategorias(alteradas){
            const mapa = { ...(this.config.mapaCategorias || {}) }
            let mudou = false

            alteradas.forEach((item) => {
                if(item.servicoId && item.categoria && mapa[item.servicoId] !== item.categoria){
                    mapa[item.servicoId] = item.categoria
                    mudou = true
                }
            })

            if(mudou){
                await salvarConfig({ ...this.config, mapaCategorias: mapa }, this.usuario)
            }
        },

        async importar(){
            this.importando = true
            try {
                const resultado = await importarDaAgenda(this.mes, this.config, this.statusImportacao)

                if(resultado.importados){
                    this.$toasted.global.defaultSuccess(`${resultado.importados} atendimento(s) importado(s)!`)
                } else {
                    this.$toasted.global.defaultSuccess('Nenhum atendimento novo para importar.')
                }

                this.dialogImportar = false
                this.$emit('recarregar')
            } catch (error) {
                this.$toasted.global.defaultError({ msg: error.message })
            } finally {
                this.importando = false
            }
        },

        async recalcular(){
            if(this.temAlteracoes && !confirm('Há alterações não salvas na tabela que serão perdidas. Continuar?')){
                return
            }

            this.recalculando = true
            try {
                const resultado = await recalcularValoresDaAgenda(this.mes, this.usuario)

                if(resultado.corrigidos){
                    this.$toasted.global.defaultSuccess(
                        `${resultado.corrigidos} lançamento(s) corrigido(s) pelo preço da agenda.`
                    )
                } else {
                    this.$toasted.global.defaultSuccess('Todos os valores já estão de acordo com a agenda.')
                }

                this.$emit('recarregar')
            } catch (error) {
                this.$toasted.global.defaultError({ msg: error.message })
            } finally {
                this.recalculando = false
            }
        },

        abrirNovo(){
            this.novo = this.lancamentoVazio()
            this.novo.data = this.dataPadraoDoMes()
            if(this.$refs.formNovo){
                this.$refs.formNovo.resetValidation()
            }
            this.dialogNovo = true
        },

        // Se o mes aberto for o corrente, ja sugere hoje; senao, o dia 1.
        dataPadraoDoMes(){
            const hoje = new Date()
            const mesAtual = `${hoje.getFullYear()}-${`${hoje.getMonth() + 1}`.padStart(2, '0')}`

            if(mesAtual === this.mes){
                return `${mesAtual}-${`${hoje.getDate()}`.padStart(2, '0')}`
            }
            return `${this.mes}-01`
        },

        async criar(){
            if(!this.$refs.formNovo.validate()){
                return
            }

            this.salvando = true
            try {
                const barbeiro = this.barbeiros.find((item) => item.id === this.novo.barbeiroId)
                await salvarReceita({
                    ...this.novo,
                    barbeiroNome: barbeiro ? barbeiro.nome : ''
                }, this.usuario)

                this.$toasted.global.defaultSuccess('Lançamento criado!')
                this.dialogNovo = false
                this.$emit('recarregar')
            } catch (error) {
                this.$toasted.global.defaultError({ msg: error.message })
            } finally {
                this.salvando = false
            }
        },

        async remover(item){
            if(!confirm(`Excluir o lançamento de ${item.cliente || 'cliente não informado'}?`)){
                return
            }

            try {
                await removerReceita(item.id)
                this.$toasted.global.defaultSuccess('Lançamento excluído!')
                this.$emit('recarregar')
            } catch (error) {
                this.$toasted.global.defaultError({ msg: error.message })
            }
        }
    }
}
</script>

<style scoped>
.secao-titulo {
    font-size: 18px;
    font-weight: 800;
    color: #14312d;
}

.campo-tabela >>> .v-input__slot {
    font-size: 13px;
}

.campo-tabela {
    margin: 0;
    padding: 0;
}

.campo-data {
    min-width: 130px;
}

.campo-select {
    min-width: 110px;
}

.campo-valor {
    min-width: 90px;
}

.celula-texto {
    min-width: 150px;
    padding: 4px 0;
}

.totais {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
}

.tabela-receitas >>> td {
    padding-top: 4px !important;
    padding-bottom: 4px !important;
}

@media (max-width: 960px) {
    .totais {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
